/**
 * Support Ticket API Service
 *
 * API methods for support ticket operations including creation, messaging, and file attachments.
 */

import apiClient from './client';
import type { ApiSuccessResponse } from '../types/api.types';
import type {
  SupportTicketSanitized,
  SupportTicketWithMessage,
  SupportTicketCreateDTO,
  SupportTicketListQuery,
  TicketMessageSanitized,
  TicketMessageCreateDTO,
  TicketMessagesResponse,
  UploadUrlResponse,
  DownloadUrlResponse,
} from '../types/support-ticket.types';

// ============================================================================
// RESPONSE TYPES
// ============================================================================

interface TicketListResponse {
  items: SupportTicketSanitized[];
  count: number;
  hasMore: boolean;
}

// ============================================================================
// SUPPORT TICKET SERVICE
// ============================================================================

export const supportTicketService = {
  /**
   * List all support tickets for a tenant
   */
  listTickets: async (
    tenantId: string,
    query?: SupportTicketListQuery
  ): Promise<TicketListResponse> => {
    // API returns: { success, data: SupportTicketSanitized[], pagination: { count, hasMore, ... } }
    const response = await apiClient.get<ApiSuccessResponse<SupportTicketSanitized[]>>(
      `/tenants/${tenantId}/support-tickets`,
      { params: query }
    );
    // Transform to expected format
    const pagination = (response.data as unknown as { pagination?: { count?: number; hasMore?: boolean } }).pagination;
    return {
      items: response.data.data,
      count: pagination?.count ?? response.data.data.length,
      hasMore: pagination?.hasMore ?? false,
    };
  },

  /**
   * Get a single support ticket by ID
   */
  getTicket: async (
    tenantId: string,
    ticketId: string
  ): Promise<SupportTicketSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<SupportTicketSanitized>>(
      `/tenants/${tenantId}/support-tickets/${ticketId}`
    );
    return response.data.data;
  },

  /**
   * Create a new support ticket
   */
  createTicket: async (
    tenantId: string,
    data: SupportTicketCreateDTO
  ): Promise<SupportTicketWithMessage> => {
    const response = await apiClient.post<ApiSuccessResponse<SupportTicketWithMessage>>(
      `/tenants/${tenantId}/support-tickets`,
      data
    );
    return response.data.data;
  },

  /**
   * List messages for a ticket
   */
  listMessages: async (
    tenantId: string,
    ticketId: string
  ): Promise<TicketMessagesResponse> => {
    // API returns: { success, data: TicketMessageSanitized[], pagination: { count, ... } }
    const response = await apiClient.get<ApiSuccessResponse<TicketMessageSanitized[]>>(
      `/tenants/${tenantId}/support-tickets/${ticketId}/messages`
    );
    // Transform to expected format
    const pagination = (response.data as unknown as { pagination?: { count?: number } }).pagination;
    return {
      items: response.data.data,
      count: pagination?.count ?? response.data.data.length,
    };
  },

  /**
   * Add a message to a ticket
   */
  createMessage: async (
    tenantId: string,
    ticketId: string,
    data: TicketMessageCreateDTO
  ): Promise<TicketMessageSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<TicketMessageSanitized>>(
      `/tenants/${tenantId}/support-tickets/${ticketId}/messages`,
      data
    );
    return response.data.data;
  },

  /**
   * Get a presigned URL for uploading an attachment
   */
  getUploadUrl: async (
    tenantId: string,
    ticketId: string,
    fileName: string,
    fileSize: number,
    mimeType: string
  ): Promise<UploadUrlResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<UploadUrlResponse>>(
      `/tenants/${tenantId}/support-tickets/${ticketId}/attachments/upload-url`,
      { fileName, fileSize, mimeType }
    );
    return response.data.data;
  },

  /**
   * Get a presigned URL for downloading an attachment
   */
  getDownloadUrl: async (
    tenantId: string,
    ticketId: string,
    attachmentId: string
  ): Promise<DownloadUrlResponse> => {
    const response = await apiClient.get<ApiSuccessResponse<DownloadUrlResponse>>(
      `/tenants/${tenantId}/support-tickets/${ticketId}/attachments/${attachmentId}/download-url`
    );
    return response.data.data;
  },

  /**
   * Upload a file to S3 using a presigned URL
   */
  uploadFile: async (
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    // Note: fetch doesn't support upload progress natively
    // For progress tracking, we'd need to use XMLHttpRequest
    if (onProgress) {
      onProgress(100);
    }
  },
};

export default supportTicketService;
